import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, TrendUp, TrendDown, Printer, Receipt } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const monthLabel = (ym) => { if(!ym) return ""; const [y,m]=ym.split("-"); return `${MONTHS_ES[parseInt(m,10)-1]} ${y}`; };

export default function FinanceBreakdown({ type }) {
  // type: "income" | "expense"
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("mode") || "accrual");
  const [months, setMonths] = useState(parseInt(params.get("months")) || 12);
  const [data, setData] = useState(null);

  useEffect(() => {
    const url = type === "income"
      ? "/reports/income-breakdown"
      : "/reports/expense-breakdown";
    const config = { params: type === "income" ? { mode, months } : { months } };
    api.get(url, config).then((r) => setData(r.data));
    const sp = new URLSearchParams();
    if (type === "income") sp.set("mode", mode);
    sp.set("months", months);
    setParams(sp, { replace: true });
  }, [mode, months, type, setParams]);

  if (!data) return <div className="p-8 text-zinc-500">Cargando…</div>;

  const isIncome = type === "income";
  const Icon = isIncome ? TrendUp : TrendDown;
  const color = isIncome ? "#00873D" : "#E0000F";
  const title = isIncome ? "Desglose de Ingresos" : "Desglose de Gastos";
  const subtitle = isIncome
    ? (mode === "accrual" ? "Notas emitidas (fecha de factura)" : "Pagos recibidos (fecha de cobro)")
    : "Gastos por fecha";

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <button onClick={()=>navigate("/finance")} className="text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1 mb-1" data-testid="back-btn">
            <ArrowLeft size={12}/> Volver al Dashboard Financiero
          </button>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">REPORTE · {months} MESES</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Icon size={28} weight="duotone" color={color}/>
            {title}
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{subtitle} · {data.total_count} {data.total_count === 1 ? "registro" : "registros"} · TC 1 USD = {data.exchange_rate} MXN</p>
        </div>
        <div className="flex items-center gap-2">
          {isIncome && (
            <div className="flex border border-zinc-200 rounded-sm overflow-hidden text-xs">
              <button onClick={()=>setMode("accrual")} className={`px-3 py-1.5 ${mode==="accrual"?"bg-zinc-900 text-white":"bg-white text-zinc-600 hover:bg-zinc-50"}`}>Acumulación</button>
              <button onClick={()=>setMode("cash")} className={`px-3 py-1.5 ${mode==="cash"?"bg-zinc-900 text-white":"bg-white text-zinc-600 hover:bg-zinc-50"}`}>Efectivo</button>
            </div>
          )}
          <Select value={String(months)} onValueChange={(v)=>setMonths(parseInt(v))}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Últimos 3 meses</SelectItem>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="24">Últimos 24 meses</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={()=>window.print()} className="gap-1">
            <Printer size={14}/> Imprimir
          </Button>
        </div>
      </header>

      <section className="border-2 p-6 bg-white" style={{ borderColor: color + "40" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Total {isIncome ? "ingresos" : "gastos"} del periodo</div>
            <div className="numeric text-5xl font-bold mt-1" style={{ color }}>MX${fmt(data.total_mxn)}</div>
          </div>
          <Icon size={64} weight="duotone" color={color} opacity={0.3}/>
        </div>
      </section>

      {data.months.length === 0 && (
        <div className="dotted-bg py-16 text-center text-zinc-400">Sin {isIncome ? "ingresos" : "gastos"} en este periodo</div>
      )}

      {data.months.map((m) => (
        <section key={m.month} className="border border-zinc-200 bg-white" data-testid={`month-${m.month}`}>
          <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Receipt size={16} className="text-zinc-400"/>
              <h3 className="font-heading text-base font-bold">{monthLabel(m.month)}</h3>
              <span className="text-xs text-zinc-500">{m.count} {m.count === 1 ? "registro" : "registros"}</span>
            </div>
            <div className="numeric text-xl font-bold" style={{ color }}>MX${fmt(m.total_mxn)}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500 bg-zinc-50/60">
              <tr>
                <th className="text-left px-4 py-2">Fecha</th>
                {isIncome ? (
                  <>
                    <th className="text-left px-4 py-2">Folio</th>
                    <th className="text-left px-4 py-2">Cliente</th>
                    {mode==="cash" && <th className="text-left px-4 py-2">Banco</th>}
                    {mode==="accrual" && <th className="text-left px-4 py-2">Estatus</th>}
                  </>
                ) : (
                  <>
                    <th className="text-left px-4 py-2">Categoría</th>
                    <th className="text-left px-4 py-2">Proveedor</th>
                    <th className="text-left px-4 py-2">Banco</th>
                  </>
                )}
                <th className="text-right px-4 py-2">Monto orig.</th>
                <th className="text-right px-4 py-2">Monto MXN</th>
              </tr>
            </thead>
            <tbody>
              {m.items.map((it, i) => (
                <tr key={`${it.sale_id || it.id || "fb"}-${i}`} className={`border-t border-zinc-100 hover:bg-zinc-50 ${isIncome ? "cursor-pointer" : ""}`}
                    onClick={()=>{ if (isIncome && it.sale_id) navigate(`/sales?open=${it.sale_id}`); }}
                    data-testid={`row-${i}`}>
                  <td className="px-4 py-2 text-zinc-600 font-mono-data text-xs">{it.date}</td>
                  {isIncome ? (
                    <>
                      <td className="px-4 py-2 font-mono-data font-semibold text-[#002FA7] hover:underline">#{String(it.folio).padStart(6,"0")}</td>
                      <td className="px-4 py-2">{it.client_name}</td>
                      {mode==="cash" && <td className="px-4 py-2 text-zinc-600 text-xs">{it.bank_name || "—"}</td>}
                      {mode==="accrual" && <td className="px-4 py-2 text-xs">
                        <span className="px-2 py-0.5 rounded-sm font-semibold" style={{
                          background: it.status==="paid"?"#00873D20":"#1E88E520",
                          color: it.status==="paid"?"#00873D":"#1E88E5",
                        }}>{it.status === "paid" ? "Pagada" : "Enviada"}</span>
                      </td>}
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm" style={{background:it.category_color+"20", color:it.category_color}}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{background:it.category_color}}/>{it.category_name}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-zinc-600">{it.vendor || "—"}</td>
                      <td className="px-4 py-2 text-zinc-600 text-xs">{it.bank_name || "—"}</td>
                    </>
                  )}
                  <td className="px-4 py-2 text-right numeric">{it.currency==="USD"?"$":"MX$"}{fmt(it.amount)}</td>
                  <td className="px-4 py-2 text-right numeric font-semibold" style={{ color }}>MX${fmt(it.amount_mxn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
